'use client';

import './payment-settings.scss';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks.js';
import {
  commitmentsQuery,
  paymentMethodsQuery,
} from '@sola/supporter-sdk/queries.js';

import { LocalAlertsProvider } from '../../molecules/local-alerts/local-alerts-provider.js';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.js';
import { PaymentMethodCard } from '../../molecules/payment-method-card/payment-method-card.js';
import { PaymentMethodAddSheet } from '../payment-method-add-sheet/payment-method-add-sheet.js';
import { SectionCardList } from '../section-card-list/section-card-list.js';
import {
  AddPaymentMethodButton,
  AddPaymentMethodButtonLabel,
} from './payment-settings-add.js';

/**
 * Get component data
 */
export const useComponentData = () => {
  const { data, isLoading, isFetching, isPending } = useSupporterQuery([
    paymentMethodsQuery,
    commitmentsQuery,
  ]);

  // @ts-expect-error  TODO We need to tighten up useSupporterQuery so these types are inferred
  const [paymentMethods, commitments]: [
    GraphQLSupporterPaymentMethod[] | undefined,
    GraphQLCommitment[] | undefined,
  ] = data;

  const commitmentsByPaymentMethodId = useMemo(() => {
    if (!commitments?.length) {
      return {};
    }
    return commitments.reduce<Record<string, GraphQLCommitment[]>>(
      (acc, commitment) => {
        const { paymentMethodId } = commitment;
        if (paymentMethodId) {
          if (!acc[paymentMethodId]) {
            acc[paymentMethodId] = [];
          }
          acc[paymentMethodId].push(commitment);
        }
        return acc;
      },
      {},
    );
  }, [commitments?.length]);

  return {
    isShowingSkeletons: isLoading || isFetching || isPending,
    paymentMethods: paymentMethods ?? [],
    commitmentsByPaymentMethodId,
  };
};

export const PaymentSettings = () => {
  const { t } = useTranslation('my');
  const { isShowingSkeletons, paymentMethods, commitmentsByPaymentMethodId } =
    useComponentData();

  return (
    <div className="PaymentSettings max-w-md">
      <LocalAlertsProvider>
        <LocalAlerts className="mb-comfy-xl" />

        <SectionCardList
          heading={t('payments.methods.heading')}
          isLoadingCards={isShowingSkeletons}
        >
          {!isShowingSkeletons && (
            <>
              {!!paymentMethods.length &&
                paymentMethods
                  // Probably unecessary.
                  .filter((method) => !!method.id)
                  .map((method) => {
                    return (
                      <PaymentMethodCard
                        key={`payment-method-card-${method.id}`}
                        paymentMethod={method}
                        commitments={
                          commitmentsByPaymentMethodId?.[method.id!] ?? []
                        }
                      />
                    );
                  })}
              <PaymentMethodAddSheet
                openTrigger={
                  <AddPaymentMethodButton>
                    <AddPaymentMethodButtonLabel>
                      {t('payments.addPaymentMethod')}
                    </AddPaymentMethodButtonLabel>
                  </AddPaymentMethodButton>
                }
              />
            </>
          )}
        </SectionCardList>
      </LocalAlertsProvider>
    </div>
  );
};
